import React, { useState } from "react";
import {
  alpha,
  CardMedia,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router";
import { IndicatorsList } from "../../../MainComponents/Indicators/IndicatorsList";
import { ProgressBar } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  parseDateToLgComun,
  transformDayJs,
} from "../../../../helpers/dateTransform";
import { EGIconButton } from "../../../MainComponents/Button/IconButton/IconButton";
import { useReportContext } from "../../ReportContext/useReportContext";
import useLocalizedLabel from "../../../../hooks/useLocalizedLabel";
import useTagsCatalog from "../../../../hooks/useTagsCatalog";
import { ReportGeneralInfo } from "../ReportGeneralInfo";
import Etiquin from "../../../../assets/images/Ethiquin/ethiquin_no_data.svg";
import { isBanamexTenant } from "../../../../config/features/banamex";
/** Reports Table */
export const ReportTable = ({ listReports, expiredReports }) => {
  // ---> Custom Hooks <--- //
  const [t] = useTranslation("report");
  const theme = useTheme();
  const navigate = useNavigate();
  const { icon_indicators, status_locales } = useReportContext();
  const getLocalizedLabel = useLocalizedLabel();
  // Loops through the array of tag objects and builds a new
  const transformedType = useTagsCatalog();
  // ---> Initial States <--- //
  // Report Summary
  const [showReportSummary, setShowReportSummary] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Area investigadora: djaguar solo la expone para los schemas de Banamex, en
  // cualquier otro tenant la columna quedaria siempre vacia.
  const showInvestigatingArea = isBanamexTenant();

  // Table columns
  const columns = [
    { id: "status", label: t("ReportsList.status"), mW: 50 },
    { id: "folio", label: t("ReportsList.folio"), mW: 50 },
    { id: "classification", label: t("ReportsList.classification"), mW: 50 },
    ...(showInvestigatingArea
      ? [{ id: "investigating_area", label: t("ReportsList.investigating_area"), mW: 50 }]
      : []),
    { id: "indicators", label: t("ReportsList.indicators"), mW: 180 },
    { id: "open_days", label: t("ReportsList.Open_days"), mW: 130 },
    //{ id: "created_at", label: t("ReportsList.created_at"), mW: 100 },
    { id: "actions", label: t("ReportsList.actions"), mW: 50 },
  ];
  // Transforms the information to display it
  const transformData = (reports) => {
    return reports.map((report) => {
      const createDate = transformDayJs(report?.created_at_time_zone);
      const closedDate = transformDayJs(report?.status?.assigned);
      let difference = null;
      if (createDate && closedDate) {
        difference = createDate.diff(closedDate, "day");
        difference = Math.abs(difference);
      }

      const current = report.status.current;
      let statusLabel = status_locales[current].label;
      let statusColor = status_locales[current].color;
      // Cuando el reporte está "closed" distinguimos el subtipo de cierre:
      // - status.details.closed => founded/unfounded/undefined (etiqueta específica, color de "closed")
      // - sin details => aún en proceso de cierre (color/label de "closing_process")
      if (current === "closed") {
        const closedKind = report?.status?.details?.closed;
        const closedLabels = {
          founded: t("ReportsList.closed_founded"),
          unfounded: t("ReportsList.closed_unfounded"),
          undefined: t("ReportsList.closed_undetermined"),
        };
        if (closedKind && closedLabels[closedKind]) {
          statusLabel = closedLabels[closedKind];
        } else {
          statusLabel = status_locales["closing_process"].label;
          statusColor = status_locales["closing_process"].color;
        }
      }

      return {
        id: report.id,
        status: statusLabel,
        statusKey: current,
        statusColor: statusColor,
        folio: report.folio,
        tracking_code: report.tracking_code,
        subject: report.subject || t("ReportsList.no_subject"),
        created_at: parseDateToLgComun(report.created_at_time_zone),
        involved: report.involved,
        difference,
        entryway: report.entryway,
        classification: report.classification,
        investigating_area: report.investigating_area,
        indicators: report.indicators,
        tags: Array.isArray(report?.tags) ? report.tags : [],
        open_days: report.open_days,
      };
    });
  };
  // Table Rows
  const rows = transformData(listReports);

  // Open report summary
  const toggleDrawer =
    (open, row = null) =>
    () => {
      setShowReportSummary(open);
      setSelectedRow(row);
    };

  return (
    <>
      {/* Report rpeview (Summary) */}
      <ReportGeneralInfo
        showReportSummary={showReportSummary}
        setShowReportSummary={setShowReportSummary}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        toggleDrawer={toggleDrawer}
        transformedType={transformedType}
        getLocalizedLabel={getLocalizedLabel}
        icon_indicators={icon_indicators}
        translation={t}
      />
      {/* Table of reports */}
      <TableContainer
        sx={{
          height: "63.33vh",
          overflowY: "auto",
          overflowX: {
            xs: "auto",
            xl: "hidden",
          },
          border: `1px solid ${alpha(theme.palette.blueGray50.main, 0.3)}`,
          borderTopLeftRadius: "6px",
          borderTopRightRadius: "6px",
        }}
      >
        {rows.length === 0 ? (
          <Stack
            sx={{
              height: "50vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CardMedia
              component="img"
              image={Etiquin}
              alt="not_found"
              sx={{ height: "45%", width: "45%", objectFit: "contain" }}
            />
            <Typography
              variant="h5"
              textAlign="center"
              color="secondary"
              mt={1.5}
            >
              {t("ReportsList.no_results_found")}...
            </Typography>
          </Stack>
        ) : (
          <Table stickyHeader aria-label="reports table list" size="small">
            <TableHead>
              <TableRow>
                {columns.map((column, idx) => (
                  <TableCell
                    key={column.id}
                    align={
                      idx === 0 || idx === columns.length - 1
                        ? "center"
                        : "left"
                    }
                    sx={{
                      minWidth: column.mW,
                      py: 1.25,
                      backgroundColor: theme.palette.blueGray50.main,
                    }}
                  >
                    <Typography variant="h6">{column.label}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => navigate(`/reports/${row.id}`)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      boxShadow: theme.shadows[1],
                    },
                  }}
                >
                  <TableCell align="center" sx={{ maxWidth: 100, px: 0.5 }}>
                    <Typography
                      variant="body2"
                      fontSize={12}
                      component="fieldset"
                      className="rounded"
                      sx={{
                        border: `solid 2px ${row.statusColor}`,
                        px: 0.5,
                        py: 0.4,
                        background: alpha(row.statusColor, 0.15),
                        width: 95,
                        mx: "auto",
                      }}
                    >
                      {row.status}
                    </Typography>
                  </TableCell>
                  <TableCell align="justify" sx={{ fontSize: 12 }}>
                    <Typography
                      variant="body2"
                      fontSize={12}
                      textAlign={{ xs: "center", xl: "left" }}
                    >
                      {row.folio}
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="inherit"
                    sx={{ fontSize: 12, maxWidth: 180 }}
                  >
                    <Typography variant="body2" fontSize={12}>
                      {row.classification ?? ""}
                    </Typography>
                  </TableCell>
                  {showInvestigatingArea && (
                    <TableCell
                      align="inherit"
                      sx={{ fontSize: 12, maxWidth: 180 }}
                    >
                      <Typography variant="body2" fontSize={12}>
                        {row.investigating_area ?? ""}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell sx={{ minWidth: 180 }}>
                    <IndicatorsList
                      indicators={row.indicators}
                      entryway={row.entryway}
                      classification={row.classification}
                      tags={row.tags}
                      transformedType={transformedType}
                      getLocalizedLabel={getLocalizedLabel}
                      icon_indicators={icon_indicators}
                      t={t}
                      theme={theme}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 130 }}>
                    {row.statusKey === "closed" ? (
                      <Typography variant="body2" fontSize={12} whiteSpace="nowrap">
                        {t("ReportsList.it_took")} {row.difference}{" "}
                        {t("ReportsList.days_to_complete")}
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="body2" fontSize={11} pl={0.5} whiteSpace="nowrap">
                          {row.open_days} {t("ReportsList.Open_days")} &gt;{" "}
                          {expiredReports} {t("ReportsList.days")}
                        </Typography>
                        <ProgressBar
                          animated
                          now={(row.open_days * 100) / expiredReports}
                          variant={
                            row.open_days > expiredReports ? "danger" : "info"
                          }
                        />
                      </>
                    )}
                  </TableCell>
                 
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <EGIconButton
                        title={t("ReportsList.summary")}
                        iconName="outline_maximize_3"
                        iconColorHover="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDrawer(true, row)();
                        }}
                        iconSize={20}
                      />
                      <EGIconButton
                        title={t("ReportsList.view_report")}
                        iconName="outline_eye"
                        iconColor="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/reports/${row.id}`);
                        }}
                        iconSize={20}
                      />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </>
  );
};
